import { SelectOption } from '@ardoq/select';
import {
  APISurveyAttributes,
  ArdoqId,
  PersistedSurveyQuestion,
  SurveyQuestionType,
} from '@ardoq/api-types';
import { exhaustiveCheck } from '@ardoq/common-helpers';
import { isDateRangeFieldType } from '@ardoq/date-range';
import { Locale } from '@ardoq/locale';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale } from '@ardoq/i18n';
import { SurveyTextSection } from '../SurveyTextSection';
import {
  Fields,
  Answers,
  PartialAnswer,
  DependentQuestionAnswer,
  ReferenceAnswer,
  FetchComponentOptionsWithQuery,
  GetAISuggestions,
  FetchTags,
  GenerateComponentDescription,
} from '../types';
import {
  getFieldTypeFromAttributeName,
  getFieldType,
} from '../utils/fieldOperations';
import { SurveyFieldQuestion } from './FieldQuestion/SurveyFieldQuestion';
import {
  getNoAccessReferenceCount,
  setupAttributeQuestionData,
  setupDateRangeFieldQuestionData,
  setupFieldQuestionData,
} from './questionOperations';
import {
  isConditionQuestion,
  isHiddenQuestion,
  isReferenceQuestion,
} from './questionPredicates';
import {
  AddNestedAnswerPayload,
  AddNestedDependentAnswerPayload,
  RemoveNestedDependentAnswerPayload,
  SetNestedAnswerPayload,
  UpdateReferenceComponentPayload,
} from './ReferenceQuestions/referenceTypes';
import { SurveyTopLevelReferenceQuestion } from './ReferenceQuestions/SurveyTopLevelReferenceQuestion';
import { SurveyAttributeQuestion } from './SurveyAttributeQuestion';
import { SurveyDateRangeFieldQuestion } from './SurveyDateRangeFieldQuestion';
import { SurveyDependentQuestions } from './SurveyDependentQuestions';
import { SurveyTagQuestion } from './TagQuestion/SurveyTagQuestion';
import {
  getNameAnswer,
  getReferenceQuestionAnswer,
  getTagQuestionAnswer,
} from '../utils/answerOperations';
import { isParentAnswer } from '../utils/answerPredicates';
import { QuestionContentWrapper } from './questionsTypes';
import { DefaultQuestionContentWrapper } from './surveyQuestionAtoms';

export type SurveyQuestionProps = {
  question: PersistedSurveyQuestion;
  isSaving: boolean;
  isLocked: boolean;
  broadcastInstanceId?: string;
  fields: Fields;
  survey: APISurveyAttributes;
  componentId: ArdoqId | undefined;
  answers: Answers | undefined;
  currentLocale: Locale;
  i18nLocale?: I18nLocale;
  hasAttemptedSubmission: boolean;
  hasAutocompleteFeature: boolean;
  hasReferenceSuggestionsFeature: boolean;
  hasEditableReferencesInQuestionsFeature: boolean;
  isEmbedded: boolean;
  forcedReadonly: boolean;
  /*
   * If true the "question is read-only" label will be hidden
   * Used in change approval tasks: see ARD-25209 for more info
   */
  hideReadOnlyLabel: boolean;
  highlightUnansweredPriorityQuestions: boolean;
  /*
   * Wrapper for the outside of question input
   * Defaults to label and description
   */
  QuestionContentWrapper?: QuestionContentWrapper;
  getDuplicate: (value: string) => null | { path: string };
  setAnswer: (answer: PartialAnswer) => void;
  setNestedAnswer: (answer: SetNestedAnswerPayload) => void;
  getExistingComponentByName: (name: string) => void;
  setDependentQuestionAnswer: (answer: DependentQuestionAnswer) => void;
  addNestedAnswer: (nestedAnswer: AddNestedAnswerPayload) => void;
  removeNestedAnswer: (nestedAnswer: {
    questionId: string;
    index: number;
  }) => void;
  addNestedDependentAnswer: (
    nestedAnswer: AddNestedDependentAnswerPayload
  ) => void;
  removeNestedDependentAnswer: (
    nestedAnswer: RemoveNestedDependentAnswerPayload
  ) => void;
  updateReferenceComponent: (
    updateReferenceComponentPayload: UpdateReferenceComponentPayload
  ) => void;
  generateComponentDescription: GenerateComponentDescription;
  trackDescriptionGenerated: (value: any) => () => void;
  trackDescriptionGeneratedFailed: () => void;
  trackCreatedReferenceFromQuestion: (
    selectedAISuggestedOption: boolean
  ) => void;
  getAISuggestions: GetAISuggestions;
  fetchComponentOptionsWithQuery: FetchComponentOptionsWithQuery;
  userOptions: SelectOption<ArdoqId>[];
  fetchTags: FetchTags;
};

export const SurveyQuestion = ({
  survey,
  question,
  componentId,
  answers = {},
  fields,
  isSaving,
  isLocked,
  currentLocale,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  hasAttemptedSubmission,
  hasAutocompleteFeature,
  hasReferenceSuggestionsFeature,
  hasEditableReferencesInQuestionsFeature,
  isEmbedded,
  forcedReadonly,
  hideReadOnlyLabel,
  highlightUnansweredPriorityQuestions,
  setAnswer,
  setNestedAnswer,
  getExistingComponentByName,
  getDuplicate,
  setDependentQuestionAnswer,
  addNestedAnswer,
  removeNestedAnswer,
  addNestedDependentAnswer,
  removeNestedDependentAnswer,
  updateReferenceComponent,
  generateComponentDescription,
  trackDescriptionGenerated,
  trackDescriptionGeneratedFailed,
  trackCreatedReferenceFromQuestion,
  getAISuggestions,
  fetchComponentOptionsWithQuery,
  userOptions,
  fetchTags,
  QuestionContentWrapper = DefaultQuestionContentWrapper,
}: SurveyQuestionProps) => {
  const readonly =
    (!isHiddenQuestion(question) && (question.readonly || forcedReadonly)) ??
    false;
  const componentName = getNameAnswer(survey.questions, answers);
  const componentTypeName = survey.componentTypeName;
  switch (question.type) {
    case SurveyQuestionType.HIDDEN_FIELD:
      return null;
    case SurveyQuestionType.TEXT:
      return <SurveyTextSection question={question} key={question._id} />;
    case SurveyQuestionType.CHILD:
      return null;
    case SurveyQuestionType.REFERENCE: {
      const answer = getReferenceQuestionAnswer(question._id, answers);
      const value = answer?.value ?? [];
      return (
        <SurveyTopLevelReferenceQuestion
          userOptions={userOptions}
          question={question}
          answer={answer}
          readonly={readonly}
          hasSubQuestions={Boolean(question.properties.questions?.length)}
          key={question._id}
          fields={fields}
          value={value}
          survey={survey}
          componentId={componentId}
          currentLocale={currentLocale}
          i18nLocale={i18nLocale}
          hasAttemptedSubmission={hasAttemptedSubmission}
          hasAutocompleteFeature={hasAutocompleteFeature}
          hasReferenceSuggestionsFeature={hasReferenceSuggestionsFeature}
          hasEditableReferencesInQuestionsFeature={
            hasEditableReferencesInQuestionsFeature
          }
          noAccessReferences={getNoAccessReferenceCount(
            answer as ReferenceAnswer | undefined
          )}
          hideReadOnlyLabel={hideReadOnlyLabel}
          highlightUnansweredPriorityQuestions={
            highlightUnansweredPriorityQuestions
          }
          setAnswer={setAnswer}
          getDuplicate={getDuplicate}
          getExistingComponentByName={getExistingComponentByName}
          addNestedAnswer={addNestedAnswer}
          setNestedAnswer={setNestedAnswer}
          removeNestedAnswer={removeNestedAnswer}
          updateReferenceComponent={updateReferenceComponent}
          isEmbedded={isEmbedded}
          generateComponentDescription={generateComponentDescription}
          trackDescriptionGenerated={trackDescriptionGenerated}
          trackDescriptionGeneratedFailed={trackDescriptionGeneratedFailed}
          trackCreatedReferenceFromQuestion={trackCreatedReferenceFromQuestion}
          getAISuggestions={getAISuggestions}
          fetchComponentOptionsWithQuery={fetchComponentOptionsWithQuery}
          QuestionContentWrapper={QuestionContentWrapper}
        />
      );
    }
    case SurveyQuestionType.TAG: {
      const answer = getTagQuestionAnswer(question._id, answers);
      const value = answer?.value ?? [];
      return (
        <SurveyTagQuestion
          surveyId={survey._id}
          question={question}
          answer={answer}
          key={question._id}
          value={value}
          currentLocale={currentLocale}
          i18nLocale={i18nLocale}
          hideReadOnlyLabel={hideReadOnlyLabel}
          QuestionContentWrapper={QuestionContentWrapper}
          questionLabel={question.label}
          highlightUnansweredPriorityQuestions={
            highlightUnansweredPriorityQuestions
          }
          setAnswer={setAnswer}
          fetchTags={fetchTags}
        />
      );
    }
    case SurveyQuestionType.ATTRIBUTE: {
      const { originalValue, validateDuplicateName, answer, value } =
        setupAttributeQuestionData({
          question,
          answers,
          isSaving,
          defaultParentId: survey.newComponentParentId,
        });
      return (
        <SurveyAttributeQuestion
          userOptions={userOptions}
          surveyId={survey._id}
          componentId={componentId}
          question={question}
          answer={answer}
          readonly={readonly}
          originalValue={originalValue}
          validateDuplicateName={validateDuplicateName}
          isLocked={isLocked}
          componentName={componentName}
          componentTypeName={componentTypeName}
          key={question._id}
          value={value}
          defaultParentId={survey.newComponentParentId}
          hasAttemptedSubmission={hasAttemptedSubmission}
          currentLocale={currentLocale}
          i18nLocale={i18nLocale}
          hasAutocompleteFeature={hasAutocompleteFeature}
          isEmbedded={isEmbedded}
          type={getFieldTypeFromAttributeName(
            question.properties.attributeName
          )}
          parentName={isParentAnswer(answer) ? answer?.parentName : undefined}
          hideReadOnlyLabel={hideReadOnlyLabel}
          QuestionContentWrapper={QuestionContentWrapper}
          highlightUnansweredPriorityQuestions={
            highlightUnansweredPriorityQuestions
          }
          generateComponentDescription={generateComponentDescription}
          onDescriptionReceived={trackDescriptionGenerated(value)}
          onLLMCouldNotGenerateDescription={trackDescriptionGeneratedFailed}
          getExistingComponentByName={getExistingComponentByName}
          setAnswer={setAnswer}
          getDuplicate={getDuplicate}
          getAISuggestions={getAISuggestions}
          fetchComponentOptionsWithQuery={fetchComponentOptionsWithQuery}
        />
      );
    }
    case SurveyQuestionType.FIELD: {
      const type = getFieldType(question.properties.fieldName, fields);
      if (isDateRangeFieldType(type)) {
        const { value, startDate, endDate, answer } =
          setupDateRangeFieldQuestionData({
            fields,
            question,
            answers,
          });
        return (
          <SurveyDateRangeFieldQuestion
            question={question}
            answer={answer}
            readonly={readonly}
            isLocked={isLocked}
            startDateField={startDate}
            endDateField={endDate}
            key={question._id}
            value={value}
            currentLocale={currentLocale}
            i18nLocale={i18nLocale}
            hideReadOnlyLabel={hideReadOnlyLabel}
            QuestionContentWrapper={QuestionContentWrapper}
            highlightUnansweredPriorityQuestions={
              highlightUnansweredPriorityQuestions
            }
            setAnswer={setAnswer}
          />
        );
      }
      const { field, answer, originalValue, validateDuplicateName, value } =
        setupFieldQuestionData({
          fields,
          question,
          answers,
          isSaving,
        });
      const hasDependentQuestions = isConditionQuestion(question);
      return (
        <SurveyFieldQuestion
          userOptions={userOptions}
          surveyId={survey._id}
          question={question}
          answer={answer}
          componentId={componentId}
          readonly={readonly}
          originalValue={originalValue}
          type={type}
          field={field}
          validateDuplicateName={validateDuplicateName}
          isLocked={isLocked}
          componentName={componentName}
          componentTypeName={componentTypeName}
          key={question._id}
          value={value}
          hasAttemptedSubmission={hasAttemptedSubmission}
          currentLocale={currentLocale}
          i18nLocale={i18nLocale}
          hasAutocompleteFeature={hasAutocompleteFeature}
          isEmbedded={isEmbedded}
          hideReadOnlyLabel={hideReadOnlyLabel}
          QuestionContentWrapper={QuestionContentWrapper}
          highlightUnansweredPriorityQuestions={
            highlightUnansweredPriorityQuestions
          }
          getExistingComponentByName={getExistingComponentByName}
          setAnswer={setAnswer}
          getDuplicate={getDuplicate}
          getAISuggestions={getAISuggestions}
          fetchComponentOptionsWithQuery={fetchComponentOptionsWithQuery}
          generateComponentDescription={generateComponentDescription}
        >
          {hasDependentQuestions ? (
            <SurveyDependentQuestions
              userOptions={userOptions}
              questions={question.properties.questions}
              componentName={componentName}
              componentTypeName={componentTypeName}
              survey={survey}
              componentId={componentId}
              fields={fields}
              parentAnswer={value}
              parentQuestionTitle={question.label}
              conditionalAnswer={answer}
              parentFieldType={type}
              parentQuestionIsReadonly={readonly}
              parentQuestionIsReferenceQuestion={isReferenceQuestion(question)}
              isLocked={isLocked}
              isSaving={isSaving}
              parentQuestionId={question._id}
              currentLocale={currentLocale}
              i18nLocale={i18nLocale}
              hasAttemptedSubmission={hasAttemptedSubmission}
              hasAutocompleteFeature={hasAutocompleteFeature}
              hasReferenceSuggestionsFeature={hasReferenceSuggestionsFeature}
              hasEditableReferencesInQuestionsFeature={
                hasEditableReferencesInQuestionsFeature
              }
              isEmbedded={isEmbedded}
              hideReadOnlyLabel={hideReadOnlyLabel}
              QuestionContentWrapper={QuestionContentWrapper}
              highlightUnansweredPriorityQuestions={
                highlightUnansweredPriorityQuestions
              }
              getDuplicate={getDuplicate}
              setAnswer={answer => {
                setDependentQuestionAnswer?.({
                  parentQuestionId: question._id,
                  answer,
                });
              }}
              setNestedAnswer={setNestedAnswer}
              getExistingComponentByName={getExistingComponentByName}
              addNestedDependentAnswer={addNestedDependentAnswer}
              removeNestedDependentAnswer={removeNestedDependentAnswer}
              updateReferenceComponent={updateReferenceComponent}
              generateComponentDescription={generateComponentDescription}
              trackDescriptionGenerated={trackDescriptionGenerated}
              trackDescriptionGeneratedFailed={trackDescriptionGeneratedFailed}
              trackCreatedReferenceFromQuestion={
                trackCreatedReferenceFromQuestion
              }
              getAISuggestions={getAISuggestions}
              fetchComponentOptionsWithQuery={fetchComponentOptionsWithQuery}
            />
          ) : null}
        </SurveyFieldQuestion>
      );
    }
    default:
      return exhaustiveCheck(question);
  }
};
